import * as React from 'react';

/**
 * Hook for local filtering in DrawerList
 */
export function useLocalDrawerSearch<T>(
  items: T[],
  searchFields: (keyof T)[],
  initialQuery = ""
) {
  const [searchQuery, setSearchQuery] = React.useState(initialQuery);

  const filteredItems = React.useMemo(() => {
    if (!searchQuery.trim()) return items;

    const query = searchQuery.toLowerCase();
    return items.filter(item => 
      searchFields.some(field => {
        const value = item[field];
        if (typeof value === 'string') {
          return value.toLowerCase().includes(query);
        }
        if (typeof value === 'number') {
          return value.toString().includes(query);
        }
        return false;
      })
    );
  }, [items, searchQuery, searchFields]);

  return {
    searchQuery,
    setSearchQuery,
    filteredItems,
  };
}

/**
 * Hook for async search with React Query integration
 * 
 * Example usage:
 * ```tsx
 * const searchHook = useAsyncDrawerSearch(
 *   (query) => searchUsersQuery({ searchTerm: query }),
 *   300 // debounce delay
 * );
 * 
 * <DrawerList
 *   searchable={true}
 *   asyncSearch={true}
 *   searchValue={searchHook.searchQuery}
 *   onSearchChange={searchHook.setSearchQuery}
 *   isLoading={searchHook.isLoading}
 * >
 *   {searchHook.data?.map(item => <Item key={item.id} {...item} />)}
 * </DrawerList>
 * ```
 */
export function useAsyncDrawerSearch<T>(
  queryFn: (query: string) => { data?: T[]; isLoading: boolean; error?: any },
  debounceDelay = 300,
  initialQuery = ""
) {
  const [searchQuery, setSearchQuery] = React.useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = React.useState(initialQuery);

  // Debounce the search query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, debounceDelay);

    return () => clearTimeout(timer);
  }, [searchQuery, debounceDelay]);

  // Execute the query with debounced search term
  const queryResult = queryFn(debouncedQuery);

  return {
    searchQuery,
    setSearchQuery,
    debouncedQuery,
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    error: queryResult.error,
  };
}

/**
 * Generic hook that works with both local and async search
 */
export function useDrawerSearch<T>(config: {
  // For local search
  items?: T[];
  searchFields?: (keyof T)[];
  // For async search  
  queryFn?: (query: string) => { data?: T[]; isLoading: boolean; error?: any };
  debounceDelay?: number;
  initialQuery?: string;
}) {
  const {
    items = [],
    searchFields = [],
    queryFn,
    debounceDelay = 300,
    initialQuery = ""
  } = config;

  const isAsync = !!queryFn;

  // Local search
  const localSearch = useLocalDrawerSearch(items, searchFields, initialQuery);
  
  // Async search
  const asyncSearch = useAsyncDrawerSearch(
    queryFn || (() => ({ data: [], isLoading: false })),
    debounceDelay,
    initialQuery
  );

  if (isAsync) {
    return {
      searchQuery: asyncSearch.searchQuery,
      setSearchQuery: asyncSearch.setSearchQuery,
      data: asyncSearch.data || [],
      isLoading: asyncSearch.isLoading,
      error: asyncSearch.error,
      isAsync: true,
    };
  }

  return {
    searchQuery: localSearch.searchQuery,
    setSearchQuery: localSearch.setSearchQuery,
    data: localSearch.filteredItems,
    isLoading: false,
    error: null,
    isAsync: false,
  };
}
